import { Injectable, inject } from '@angular/core';
import { EXERCISES } from '../exercises/exercise-generator';
import { isEarlyChildhoodProfile } from '../models/profile-stage';
import { LocalLearningStore } from '../storage/local-learning-store';
import { buildDescriptiveProgressRows, buildProgressBySkill } from './descriptive-progress';

const PANEL_SELECTOR = '[data-adult-progress-summary]';
const TARGET_SELECTOR = '.activity-menu-card .adult-tools, .complete-card .adult-tools';

@Injectable({ providedIn: 'root' })
export class AdultProgressSummaryService {
  private readonly store = inject(LocalLearningStore);
  private observer: MutationObserver | null = null;
  private syncQueued = false;
  private renderToken = 0;

  start(): void {
    if (
      typeof document === 'undefined' ||
      typeof MutationObserver === 'undefined' ||
      this.observer
    ) {
      return;
    }

    const root = document.querySelector('app-root');
    if (!root) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target;
        return !(target instanceof Element && target.closest(PANEL_SELECTOR));
      });
      if (relevant) {
        this.scheduleSync();
      }
    });
    this.observer.observe(root, { childList: true, subtree: true });
    this.scheduleSync();
  }

  private scheduleSync(): void {
    if (this.syncQueued) {
      return;
    }
    this.syncQueued = true;
    queueMicrotask(() => {
      this.syncQueued = false;
      void this.sync();
    });
  }

  private async sync(): Promise<void> {
    const targets = [...document.querySelectorAll<HTMLElement>(TARGET_SELECTOR)];
    if (!targets.length) {
      return;
    }

    for (const target of targets) {
      let panel = target.querySelector<HTMLDetailsElement>(PANEL_SELECTOR);
      if (!panel) {
        panel = this.createPanel();
        target.prepend(panel);
      }
      await this.refreshPanel(panel);
    }
  }

  private createPanel(): HTMLDetailsElement {
    const panel = document.createElement('details');
    panel.className = 'adult-progress-summary';
    panel.setAttribute('data-adult-progress-summary', 'true');

    const summary = document.createElement('summary');
    summary.textContent = 'Progressi locali';

    const boundary = document.createElement('p');
    boundary.className = 'adult-progress-boundary';
    boundary.textContent =
      'Questi numeri descrivono solo i tentativi salvati sul dispositivo. Non indicano livello, padronanza, prontezza o cosa fare dopo.';

    const rows = document.createElement('div');
    rows.className = 'adult-progress-rows';
    rows.setAttribute('data-adult-progress-rows', 'true');
    rows.setAttribute('aria-live', 'polite');

    panel.append(summary, boundary, rows);
    return panel;
  }

  private async refreshPanel(panel: HTMLDetailsElement): Promise<void> {
    const token = ++this.renderToken;
    const rowsHost = panel.querySelector<HTMLElement>('[data-adult-progress-rows]');
    if (!rowsHost) {
      return;
    }

    const activeProfileId = await this.store.getActiveProfileId();
    if (!activeProfileId) {
      panel.remove();
      return;
    }

    const profiles = await this.store.listProfiles();
    const profile = profiles.find((candidate) => candidate.id === activeProfileId);
    if (!profile || isEarlyChildhoodProfile(profile)) {
      panel.remove();
      return;
    }

    const descriptors = EXERCISES
      .filter((exercise) => profile.age >= exercise.minAge && profile.age <= exercise.maxAge)
      .map((exercise) => ({ skillId: exercise.skillId, label: exercise.title }));
    const attempts = await this.store.listAttempts();
    const progressBySkill = buildProgressBySkill(attempts, activeProfileId);
    const rows = buildDescriptiveProgressRows(descriptors, progressBySkill);

    if (token !== this.renderToken || !panel.isConnected) {
      return;
    }

    rowsHost.replaceChildren(...rows.map((row) => this.renderRow(row.label, row.attempts, row.correct)));
  }

  private renderRow(label: string, attempts: number, correct: number): HTMLElement {
    const row = document.createElement('article');
    row.className = 'adult-progress-row';

    const title = document.createElement('strong');
    title.textContent = label;

    const counts = document.createElement('span');
    counts.textContent = attempts === 0
      ? 'Nessun tentativo registrato.'
      : `${attempts} ${attempts === 1 ? 'tentativo' : 'tentativi'} · ${correct} ${correct === 1 ? 'risposta corretta' : 'risposte corrette'}`;

    row.append(title, counts);
    return row;
  }
}
